import { useCallback } from 'react';
import { useDateFnsLocale } from '../useDateFnsLocale';
import {
  format as dateFnsFormat,
  formatDistanceStrict as dateFnsFormatDistanceStrict,
  formatDistanceToNow as dateFnsFormatDistanceToNow,
} from 'date-fns';
import { getUTCDate } from '@/utilities/date';

/**
 *
 * `useDateFormatter` is a custom hook that provides locale-aware date formatting functions.
 * It uses the locale from `useDateFnsLocale` hook and returns functions from `date-fns`
 *
 */
export function useDateFormatter() {
  const { locale } = useDateFnsLocale();

  const format = useCallback(
    (
      date: Date | number | string,
      formatStr = 'dd/MM/yyyy',
      options?: Parameters<typeof dateFnsFormat>[2]
    ) => {
      return dateFnsFormat(date, formatStr, {
        ...options,
        locale,
      });
    },
    [locale]
  );

  const formatUTC = useCallback(
    (
      date: Date,
      formatStr: string,
      options?: Parameters<typeof dateFnsFormat>[2]
    ) => {
      const utcDate = getUTCDate(date.getTime());

      return dateFnsFormat(utcDate, formatStr, {
        ...options,
        locale,
      });
    },
    [locale]
  );

  const formatDistanceStrict = useCallback<typeof dateFnsFormatDistanceStrict>(
    (date, baseDate, options) => {
      return dateFnsFormatDistanceStrict(date, baseDate, {
        ...options,
        locale,
      });
    },
    [locale]
  );

  const formatDistanceToNow = useCallback<typeof dateFnsFormatDistanceToNow>(
    (date, options) => {
      return dateFnsFormatDistanceToNow(date, { ...options, locale });
    },
    [locale]
  );

  return {
    format,
    formatUTC,
    formatDistanceStrict,
    formatDistanceToNow,
  };
}
